import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import ErrorBoundary from './components/ErrorBoundary';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PreferencesProvider } from './contexts/PreferencesContext';
import { ClientAuthProvider, useClientAuth } from './contexts/ClientAuthContext';
import { SocketProvider } from './contexts/SocketContext';
import Layout from './components/Layout';
import usePageViewTracking from './hooks/usePageViewTracking';
import Login from './pages/Login';
import ClientLogin from './pages/client/ClientLogin';
import ClientLayout from './pages/client/ClientLayout';
import ClientDashboard from './pages/client/ClientDashboard';
import Dashboard from './pages/Dashboard';
import Projects from './pages/Projects';
import ActiveProjects from './pages/ActiveProjects';
import ActiveCalendar from './pages/ActiveCalendar';
import ProjectDetail from './pages/ProjectDetail';
import Engagements from './pages/Engagements';
import EngagementDetail from './pages/EngagementDetail';
import ClosedJobs from './pages/ClosedJobs';
import ManufacturerPurchases from './pages/ManufacturerPurchases';
import LoginActivity from './pages/LoginActivity';
import NewSalesBySalesperson from './pages/NewSalesBySalesperson';
import Tasks from './pages/Tasks';
import Calendar from './pages/Calendar';
import ARReport from './pages/ARReport';
import ProposalReview from './pages/ProposalReview';
import ClientExecutiveSummary from './pages/exec-summary/ClientExecutiveSummary';
import ClientHoursRecording from './pages/ClientHoursRecording';
import ArCollected from './pages/ArCollected';
import LaborVariance from './pages/LaborVariance';
import NewBusiness from './pages/NewBusiness';
import MTDReconciler from './pages/MTDReconciler';
import ChangeOrders from './pages/ChangeOrders';
import Notifications from './pages/Notifications';
import Reports from './pages/Reports';
import Dispatch from './pages/Dispatch';
import DispatchTemplate from './pages/DispatchTemplate';
import DispatchPreview from './pages/DispatchPreview';
import BillingQueue from './pages/BillingQueue';
import DispatchOnCall from './pages/DispatchOnCall';
import Assignments from './pages/Assignments';
import Settings from './pages/Settings';
import ClientMessages from './pages/ClientMessages';
import ClientBrowse from './pages/ClientBrowse';
import AskACS from './pages/AskACS';
import Warehouse from './pages/Warehouse';
import MfgSwap   from './pages/MfgSwap';
import PricingUpload from './pages/PricingUpload';
import ProductSearch from './pages/ProductSearch';
import DtoolsCrm from './pages/DtoolsCrm';
import PurchaseOrders from './pages/PurchaseOrders';
import CompanyTimeline from './pages/CompanyTimeline';
import AfterCare from './pages/AfterCare';
import LaborUtilization from './pages/LaborUtilization';
import ITTeamUtilization from './pages/ITTeamUtilization';
import LaborProgress from './pages/LaborProgress';
import CRM from './pages/CRM';
import Clients from './pages/Clients';
import Messaging from './pages/Messaging';
import SalesPipeline from './pages/SalesPipeline';
import SalesForecast from './pages/SalesForecast';
import DailyReport from './pages/DailyReport';
import PMDashboard from './pages/PMDashboard';
import PMWorkload from './pages/PMWorkload';
import Directory from './pages/Directory';
import FieldGuides from './pages/FieldGuides';
import Tickets from './pages/Tickets';
import Soundcheck from './pages/Soundcheck';
import SoundcheckShare from './pages/SoundcheckShare';
import { TicketDialogProvider } from './components/TicketDialog';
import ResetPassword from './pages/ResetPassword';
import SageRecon from './pages/SageRecon';
import WeeklyActivity from './pages/WeeklyActivity';
import { CircularProgress, Box } from '@mui/material';

const theme = createTheme({
  palette: {
    primary: {
      main: '#1A5490',
      light: '#4A7FB5',
      dark: '#0D2C54',
    },
    secondary: {
      main: '#FF6B35',
    },
    info: {
      main: '#00A8B5',
    },
    success: {
      main: '#2e7d32',
    },
    warning: {
      main: '#f57c00',
    },
    error: {
      main: '#c62828',
    },
  },
  typography: {
    fontFamily: '"Heebo", "Montserrat", "Roboto", "Helvetica", "Arial", sans-serif',
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: {
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 4,
        },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: {
          borderRadius: 8,
        },
      },
    },
  },
});

const PrivateRoute = ({ children }) => {
  const { user, loading } = useAuth();
  const location = useLocation();

  // Silent page-view tracking. Fires only when authenticated; errors swallowed.
  // Tom 2026-05-09: usability curiosity, owner-only visibility.
  usePageViewTracking(!!user);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // When bouncing an unauthenticated user to /login, remember where they
  // were trying to go so Login can restore it after successful auth.
  // Wrap the page content in an ErrorBoundary keyed on the route so a
  // crashing page doesn't white-screen the whole app and the user can keep
  // using the sidebar / navigate away. The boundary auto-resets when the
  // path changes (resetKey prop).
  return user
    ? (
      <Layout>
        <ErrorBoundary resetKey={location.pathname}>
          {children}
        </ErrorBoundary>
      </Layout>
    )
    : <Navigate to="/login" state={{ from: location }} replace />;
};

// OwnerOnly: hard route gate that redirects anyone but Tom (id 3) to
// /dashboard before the page even renders. Belt-and-suspenders alongside
// the backend ownerOnly middleware. Tom 2026-05-09: 'Not one single user
// should have access to this activity but me.'
const OwnerOnly = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!user || user.id !== 3) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

// SalesReportGate: narrow allowlist for /admin/new-sales-by-salesperson.
// Mirrors the backend ALLOWED_USER_IDS in routes/sales-by-salesperson.js.
// Tom (3) + Robert Kaufman (1) + Jonathan Flamm (2) + Chris Georghiou (4)
// per Tom 2026-06-17. Add IDs here AND in the backend route to grant.
const SALES_REPORT_ALLOWED_IDS = new Set([3, 1, 2, 4]);
const SalesReportGate = ({ children }) => {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (!user || !SALES_REPORT_ALLOWED_IDS.has(user.id)) {
    return <Navigate to="/dashboard" replace />;
  }
  return children;
};

const PublicRoute = ({ children }) => {
  const { user, loading } = useAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return user ? <Navigate to="/" /> : children;
};

// Client portal route guards — completely separate from employee auth
const ClientPrivateRoute = ({ children }) => {
  const { clientUser, loading } = useClientAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  // Same containment story as the internal app: a crashing portal page must
  // not white-screen the whole tree. The boundary auto-resets when the route
  // pathname changes so navigating away clears the error.
  return clientUser
    ? <ErrorBoundary resetKey={location.pathname}>{children}</ErrorBoundary>
    : <Navigate to="/portal/login" />;
};

const ClientPublicRoute = ({ children }) => {
  const { clientUser, loading } = useClientAuth();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return clientUser ? <Navigate to="/portal" /> : children;
};

// Redirect AfterCare roles to /aftercare instead of Dashboard
const AfterCareRedirect = () => {
  const { user } = useAuth();
  if (user?.role === 'AfterCare' || user?.role === 'AfterCare Technician') {
    return <Navigate to="/aftercare" />;
  }
  return <Dashboard />;
};

// Soundcheck route: public URL that works with or without an ACS login.
// Logged-in users get the full Layout (sidebar, top bar) so it feels like
// any other Command Center page. Anonymous visitors get just the page —
// no sidebar, no login redirect.
const SoundcheckRoute = () => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  if (user) {
    return (
      <Layout>
        <ErrorBoundary resetKey={location.pathname}>
          <Soundcheck />
        </ErrorBoundary>
      </Layout>
    );
  }
  return (
    <ErrorBoundary resetKey={location.pathname}>
      <Soundcheck />
    </ErrorBoundary>
  );
};

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <ClientAuthProvider>
      <AuthProvider>
        <SocketProvider>
        <PreferencesProvider>
        <TicketDialogProvider>
        <Router>
          <Routes>
            {/* ── Client Portal (separate auth, no sidebar) ── */}
            <Route
              path="/portal/login"
              element={
                <ClientPublicRoute>
                  <ClientLogin />
                </ClientPublicRoute>
              }
            />
            <Route
              path="/portal"
              element={
                <ClientPrivateRoute>
                  <ClientLayout>
                    <ClientDashboard />
                  </ClientLayout>
                </ClientPrivateRoute>
              }
            />
            {/* ── Internal Platform ─────────────────────────── */}
            <Route
              path="/login"
              element={
                <PublicRoute>
                  <Login />
                </PublicRoute>
              }
            />
            <Route
              path="/"
              element={
                <PrivateRoute>
                  <AfterCareRedirect />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects"
              element={
                <PrivateRoute>
                  <Projects />
                </PrivateRoute>
              }
            />
            <Route
              path="/wip/active"
              element={
                <PrivateRoute>
                  <ActiveProjects />
                </PrivateRoute>
              }
            />
            <Route
              path="/wip/calendar"
              element={
                <PrivateRoute>
                  <ActiveCalendar />
                </PrivateRoute>
              }
            />
            <Route
              path="/projects/:id"
              element={
                <PrivateRoute>
                  <ProjectDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/engagements"
              element={
                <PrivateRoute>
                  <Engagements />
                </PrivateRoute>
              }
            />
            <Route
              path="/engagements/:id"
              element={
                <PrivateRoute>
                  <EngagementDetail />
                </PrivateRoute>
              }
            />
            <Route
              path="/closed-jobs"
              element={
                <PrivateRoute>
                  <ClosedJobs />
                </PrivateRoute>
              }
            />
            <Route
              path="/manufacturer-purchases"
              element={
                <PrivateRoute>
                  <ManufacturerPurchases />
                </PrivateRoute>
              }
            />
            {/* Owner-only login activity dashboard. Three layers of
                enforcement: (1) backend ownerOnly middleware returns 404
                to anyone but Tom; (2) sidebar item is hidden for
                non-owners; (3) OwnerOnly route wrapper redirects to
                /dashboard if a non-owner navigates to the URL directly. */}
            <Route
              path="/admin/login-activity"
              element={
                <PrivateRoute>
                  <OwnerOnly>
                    <LoginActivity />
                  </OwnerOnly>
                </PrivateRoute>
              }
            />
            {/* New Sales by Salesperson — narrow allowlist (Tom + Robert +
                Jonathan + Chris). Tom 2026-06-17. Backend mirror lives in
                server/routes/sales-by-salesperson.js ALLOWED_USER_IDS. */}
            <Route
              path="/admin/new-sales-by-salesperson"
              element={
                <PrivateRoute>
                  <SalesReportGate>
                    <NewSalesBySalesperson />
                  </SalesReportGate>
                </PrivateRoute>
              }
            />
            {/* PM Workload — active projects/jobs per PM with discipline
                breakdown + dollar value + project status. Tom 2026-06-18. */}
            <Route
              path="/admin/pm-workload"
              element={
                <PrivateRoute>
                  <PMWorkload />
                </PrivateRoute>
              }
            />
            {/* Project Timeline - wrapped in PrivateRoute for sidebar */}
            <Route
              path="/calendar"
              element={
                <PrivateRoute>
                  <Calendar />
                </PrivateRoute>
              }
            />
            {/* /dispatch is the live calendar. Preview/legacy routes retired
                2026-11-02 at Tom's direction. */}
            <Route
              path="/dispatch"
              element={
                <PrivateRoute>
                  <DispatchPreview />
                </PrivateRoute>
              }
            />
            <Route
              path="/dispatch/on-call"
              element={
                <PrivateRoute>
                  <DispatchOnCall />
                </PrivateRoute>
              }
            />
            {/* Legacy aliases — redirect any stale bookmarks to /dispatch. */}
            <Route path="/dispatch-preview" element={<Navigate to="/dispatch" replace />} />
            <Route path="/dispatch-preview/on-call" element={<Navigate to="/dispatch/on-call" replace />} />
            <Route path="/dispatch-legacy" element={<Navigate to="/dispatch" replace />} />
            {/* /dispatch-template now serves the new Dispatch Preview build.
                Old DispatchTemplate.js preserved for one-week revert window at /dispatch-template-legacy. */}
            <Route
              path="/dispatch-template"
              element={
                <PrivateRoute>
                  <DispatchPreview />
                </PrivateRoute>
              }
            />
            <Route
              path="/dispatch-template/on-call"
              element={
                <PrivateRoute>
                  <DispatchOnCall />
                </PrivateRoute>
              }
            />
            <Route
              path="/dispatch-template-legacy"
              element={
                <PrivateRoute>
                  <DispatchTemplate />
                </PrivateRoute>
              }
            />
            <Route
              path="/billing-queue"
              element={
                <PrivateRoute>
                  <BillingQueue />
                </PrivateRoute>
              }
            />
            <Route
              path="/ar-report"
              element={
                <PrivateRoute>
                  <ARReport />
                </PrivateRoute>
              }
            />
            <Route
              path="/proposal-review"
              element={
                <PrivateRoute>
                  <ProposalReview />
                </PrivateRoute>
              }
            />
            <Route
              path="/exec-summary"
              element={
                <PrivateRoute>
                  <ClientExecutiveSummary />
                </PrivateRoute>
              }
            />
            <Route
              path="/client-hours-recording"
              element={
                <PrivateRoute>
                  <ClientHoursRecording />
                </PrivateRoute>
              }
            />
            <Route
              path="/sage-recon"
              element={
                <PrivateRoute>
                  <SageRecon />
                </PrivateRoute>
              }
            />
            <Route
              path="/weekly-activity"
              element={
                <PrivateRoute>
                  <WeeklyActivity />
                </PrivateRoute>
              }
            />
            <Route
              path="/ar-collected"
              element={
                <PrivateRoute>
                  <ArCollected />
                </PrivateRoute>
              }
            />
            <Route
              path="/labor-variance"
              element={
                <PrivateRoute>
                  <LaborVariance />
                </PrivateRoute>
              }
            />
            <Route
              path="/new-business"
              element={
                <PrivateRoute>
                  <NewBusiness />
                </PrivateRoute>
              }
            />
            {/* MTD Cash Received reconciler — Management + Finance only (page enforces role). */}
            <Route
              path="/mtd-reconciler"
              element={
                <PrivateRoute>
                  <MTDReconciler />
                </PrivateRoute>
              }
            />
            <Route
              path="/tasks"
              element={
                <PrivateRoute>
                  <Tasks />
                </PrivateRoute>
              }
            />
            <Route
              path="/change-orders"
              element={
                <PrivateRoute>
                  <ChangeOrders />
                </PrivateRoute>
              }
            />
            <Route
              path="/notifications"
              element={
                <PrivateRoute>
                  <Notifications />
                </PrivateRoute>
              }
            />
            <Route
              path="/reports"
              element={
                <PrivateRoute>
                  <Reports />
                </PrivateRoute>
              }
            />
            <Route
              path="/assignments"
              element={
                <PrivateRoute>
                  <Assignments />
                </PrivateRoute>
              }
            />
            <Route
              path="/settings"
              element={
                <PrivateRoute>
                  <Settings />
                </PrivateRoute>
              }
            />
            <Route
              path="/client-messages"
              element={
                <PrivateRoute>
                  <ClientMessages />
                </PrivateRoute>
              }
            />
            <Route
              path="/client-portal-preview"
              element={
                <PrivateRoute>
                  <ClientBrowse />
                </PrivateRoute>
              }
            />
            <Route
              path="/ask"
              element={
                <PrivateRoute>
                  <AskACS />
                </PrivateRoute>
              }
            />
            <Route
              path="/warehouse"
              element={
                <PrivateRoute>
                  <Warehouse />
                </PrivateRoute>
              }
            />
            <Route
              path="/warehouse/mfg-swap"
              element={
                <PrivateRoute>
                  <MfgSwap />
                </PrivateRoute>
              }
            />
            <Route
              path="/warehouse/pricing-upload"
              element={
                <PrivateRoute>
                  <PricingUpload />
                </PrivateRoute>
              }
            />
            <Route
              path="/product-search"
              element={
                <PrivateRoute>
                  <ProductSearch />
                </PrivateRoute>
              }
            />
            <Route
              path="/dtools-crm"
              element={
                <PrivateRoute>
                  <DtoolsCrm />
                </PrivateRoute>
              }
            />
            <Route
              path="/company-timeline"
              element={
                <PrivateRoute>
                  <CompanyTimeline />
                </PrivateRoute>
              }
            />
            <Route
              path="/aftercare"
              element={
                <PrivateRoute>
                  <AfterCare />
                </PrivateRoute>
              }
            />
            <Route
              path="/purchase-orders"
              element={
                <PrivateRoute>
                  <PurchaseOrders />
                </PrivateRoute>
              }
            />
            <Route
              path="/labor-utilization"
              element={
                <PrivateRoute>
                  <LaborUtilization />
                </PrivateRoute>
              }
            />
            <Route
              path="/it-team-utilization"
              element={
                <PrivateRoute>
                  <ITTeamUtilization />
                </PrivateRoute>
              }
            />
            <Route
              path="/labor-progress"
              element={
                <PrivateRoute>
                  <LaborProgress />
                </PrivateRoute>
              }
            />
            <Route
              path="/crm"
              element={
                <PrivateRoute>
                  <CRM />
                </PrivateRoute>
              }
            />
            <Route
              path="/clients"
              element={
                <PrivateRoute>
                  <Clients />
                </PrivateRoute>
              }
            />
            <Route
              path="/clients/:arCode"
              element={
                <PrivateRoute>
                  <Clients />
                </PrivateRoute>
              }
            />
            <Route
              path="/sales-pipeline"
              element={
                <PrivateRoute>
                  <SalesPipeline />
                </PrivateRoute>
              }
            />
            <Route
              path="/sales-pipeline/forecast"
              element={
                <PrivateRoute>
                  <SalesForecast />
                </PrivateRoute>
              }
            />
            <Route
              path="/messages"
              element={
                <PrivateRoute>
                  <Messaging />
                </PrivateRoute>
              }
            />
            <Route
              path="/daily-report"
              element={
                <PrivateRoute>
                  <DailyReport />
                </PrivateRoute>
              }
            />
            <Route
              path="/pm-dashboard"
              element={
                <PrivateRoute>
                  <PMDashboard />
                </PrivateRoute>
              }
            />
            <Route
              path="/directory"
              element={
                <PrivateRoute>
                  <Directory />
                </PrivateRoute>
              }
            />
            <Route
              path="/field-guides"
              element={
                <PrivateRoute>
                  <FieldGuides />
                </PrivateRoute>
              }
            />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route
              path="/tickets"
              element={
                <PrivateRoute>
                  <Tickets />
                </PrivateRoute>
              }
            />
            {/* Soundcheck is a public tool (Tom 2026-07-25). Logged-in ACS
                users get it inside the normal Layout (sidebar visible);
                anonymous visitors get a clean standalone shell. Track
                history is scoped per-identity so anonymous sessions never
                see each other's tracks. */}
            <Route path="/soundcheck" element={<SoundcheckRoute />} />
            {/* Public share page — no auth. */}
            <Route path="/soundcheck/share/:uuid" element={<SoundcheckShare />} />
            <Route path="*" element={<Navigate to="/" />} />
          </Routes>
        </Router>
        </TicketDialogProvider>
        </PreferencesProvider>
        </SocketProvider>
      </AuthProvider>
      </ClientAuthProvider>
    </ThemeProvider>
  );
}

export default App;
